import { parseDicomDateTime } from '../dicom/utils';

/**
 * Resolved PET metadata for SUV computation.
 * Time fields are pre-resolved to single date+time values
 * by DicomImageSlice — no fallback logic here.
 */
export interface PetSeriesMetadata {
    /** Patient weight in kilograms (0010,1030) */
    patientWeightKg?: number;
    /** Radionuclide total dose in Bq (0054,0016)[0].(0018,1074) */
    radionuclideTotalDose?: number;
    /** Radionuclide half life in seconds (0054,0016)[0].(0018,1075) */
    radionuclideHalfLife?: number;
    /** Resolved scan datetime as DICOM DateTime string (YYYYMMDDHHMMSS.FFFFFF) */
    scanDateTime?: string;
    /** Resolved injection datetime as DICOM DateTime string (YYYYMMDDHHMMSS.FFFFFF) */
    injectionDateTime?: string;
}

export function computeSUVbwFactor(
    petMetadata: PetSeriesMetadata
): number | null {
    const {
        patientWeightKg,
        radionuclideTotalDose,
        radionuclideHalfLife,
        scanDateTime,
        injectionDateTime,
    } = petMetadata;

    if (!patientWeightKg || patientWeightKg <= 0) {
        return null;
    }
    if (!radionuclideTotalDose || radionuclideTotalDose <= 0) {
        return null;
    }
    if (!radionuclideHalfLife || radionuclideHalfLife <= 0) {
        return null;
    }
    if (!scanDateTime || !injectionDateTime) {
        return null;
    }

    const elapsedSeconds =
        parseDicomDateTime(scanDateTime) -
        parseDicomDateTime(injectionDateTime);

    // A present-but-malformed timing tag makes parseDicomDateTime return NaN;
    // `NaN < 0` is false, so guard non-finite explicitly rather than let a NaN
    // propagate into (and silently defeat) every downstream threshold guard.
    if (!Number.isFinite(elapsedSeconds) || elapsedSeconds < 0) {
        return null;
    }

    const decayedDose =
        radionuclideTotalDose *
        Math.pow(2, -elapsedSeconds / radionuclideHalfLife);

    if (decayedDose <= 0) {
        return null;
    }

    const factor = (patientWeightKg * 1000) / decayedDose;
    // Never surface a non-finite / non-positive factor as a usable SUV.
    return Number.isFinite(factor) && factor > 0 ? factor : null;
}

/**
 * Checks which DICOM parameters required for SUV computation are missing.
 * When every input is present but the factor is still not computable, the
 * timing pair is inconsistent (scan before injection, or malformed) — that
 * case gets its own entry so the "why not" list is never unhelpfully empty.
 */
export function getMissingSUVParams(petMetadata: PetSeriesMetadata): string[] {
    const missing: string[] = [];

    if (!petMetadata.radionuclideTotalDose) {
        missing.push('Radionuclide Total Dose (0018,1074)');
    }
    if (!petMetadata.radionuclideHalfLife) {
        missing.push('Radionuclide Half Life (0018,1075)');
    }
    if (!petMetadata.patientWeightKg) {
        missing.push('Patient Weight (0010,1030)');
    }
    if (!petMetadata.scanDateTime) {
        missing.push('Scan Date/Time (0008,0022+0008,0032)');
    }
    if (!petMetadata.injectionDateTime) {
        missing.push(
            'Radiopharmaceutical Start DateTime (0018,1078/0018,1072)'
        );
    }
    if (missing.length === 0 && computeSUVbwFactor(petMetadata) === null) {
        missing.push('Scan/injection timing inconsistent');
    }

    return missing;
}

/**
 * How this volume's stored voxel values relate to SUVbw.
 *
 * - `bqml` — activity concentration decay-corrected to scan start;
 *   SUV = raw × {@link SuvQuantification.factor}.
 * - `preconverted-gml` — the vendor already wrote SUVbw g/ml pixels
 *   (`Units=GML` with `SUVType` BW or absent — absent implies BW, the
 *   same inference mvdicom's classifier applies); factor is 1.
 * - `preconverted-other` — GML pixels that are NOT usable as SUVbw:
 *   normalized to something other than body weight (`SUVType`
 *   LBM/BSA/IBW…), or missing the required image corrections
 *   (`CorrectedImage` lacking ATTN/DECY, or absent). Honest g/ml
 *   display, but the SUVbw tooling refuses.
 * - `counts` — `CNTS`; no SUV interpretation without vendor private
 *   tags (not implemented).
 * - `not-quantified` — non-PT, absent/other Units, non-START decay
 *   correction, missing conversion inputs, or a degenerate intensity
 *   range; {@link SuvQuantification.missingParams} carries the reasons.
 */
export type SuvQuantificationState =
    | 'bqml'
    | 'preconverted-gml'
    | 'preconverted-other'
    | 'counts'
    | 'not-quantified';

/**
 * The single durable record of a volume's SUV quantification state,
 * classified once per image ({@link Image3D.suvQuantification}) so every
 * consumer — readouts, W/L, colorwash, drawing thresholds, the SUV tool,
 * provenance stamping — reads one truth instead of re-deriving it from
 * the factor alone (which is exactly what let pre-converted series show
 * double-converted numbers labeled SUVbw).
 */
export interface SuvQuantification {
    readonly state: SuvQuantificationState;
    /** `Units` (0054,1001), trimmed and uppercased; `''` when absent. */
    readonly units: string;
    /** `DecayCorrection` (0054,1102), trimmed/uppercased; `''` when absent. */
    readonly decayCorrection: string;
    /** `SUVType` (0054,1006), trimmed/uppercased; null when absent. */
    readonly suvType: string | null;
    /** raw → SUVbw multiplier. Non-null only for `bqml` (computed) and
     *  `preconverted-gml` (identity, 1). */
    readonly factor: number | null;
    /** The conversion inputs, for tooltips and provenance stamping. */
    readonly params: PetSeriesMetadata | null;
    /** Human-readable reasons the state is not SUVbw-usable; empty for
     *  the quantified states. */
    readonly missingParams: readonly string[];
}

const NOT_QUANTIFIED_BASE = {
    state: 'not-quantified',
    suvType: null,
    factor: null,
    params: null,
} as const;

/**
 * The image corrections `CorrectedImage` (0028,0051) must declare before
 * ANY stored PET value may be read as SUV: without attenuation correction
 * the values are depth-dependently attenuated and quantitatively
 * meaningless, and without decay correction the decay term of the SUVbw
 * formula is wrong. QIBA's vendor-neutral pseudocode checks exactly these
 * two; mvdicom (ct_mr.py:1410-1417) additionally requires RAN/SCAT/NORM,
 * which essentially every clinical reconstruction applies — ATTN and DECY
 * are the discriminating pair (they are what separates an AC from a NAC
 * export of the same acquisition).
 */
const REQUIRED_IMAGE_CORRECTIONS = ['ATTN', 'DECY'] as const;

/**
 * Reasons the `CorrectedImage` (0028,0051) attribute disqualifies the
 * volume from ANY SUV reading; empty when the required corrections are
 * declared. Absent or zero-length (the attribute is Type 2 in the PET
 * Series Module, PS3.3 Table C.8-60) means the applied corrections are
 * UNKNOWN — refused rather than assumed, mirroring mvdicom, which
 * checks this attribute before every SUV path.
 */
function getMissingImageCorrections(
    correctedImage: readonly string[] | null
): string[] {
    if (correctedImage === null) {
        return ['CorrectedImage (0028,0051) absent'];
    }
    return REQUIRED_IMAGE_CORRECTIONS.filter(
        (correction) => !correctedImage.includes(correction)
    ).map((correction) => `CorrectedImage lacks ${correction}`);
}

/**
 * Pure classification of a volume's SUV state — the one place the
 * Units / DecayCorrection / SUVType / factor rules live. Callers gate
 * behavior on {@link SuvQuantification.state} (or the derived
 * {@link Image3D.isSuvQuantified} / {@link Image3D.suvFactor} views),
 * never on tag values directly.
 */
export function classifySuvQuantification(input: {
    isPt: boolean;
    units: string;
    decayCorrection: string;
    suvType: string;
    /** `CorrectedImage` (0028,0051) values, uppercased; `null` = absent. */
    correctedImage: readonly string[] | null;
    petMetadata: PetSeriesMetadata | null;
    /** minValue < maxValue — a flat volume has no max to threshold against. */
    hasIntensityRange: boolean;
}): SuvQuantification {
    const units = input.units.trim().toUpperCase();
    const decayCorrection = input.decayCorrection.trim().toUpperCase();
    const suvType = input.suvType.trim().toUpperCase() || null;
    const missingCorrections = getMissingImageCorrections(input.correctedImage);

    if (!input.isPt) {
        return {
            ...NOT_QUANTIFIED_BASE,
            units,
            decayCorrection,
            missingParams: [],
        };
    }

    const base = { units, decayCorrection, suvType, params: input.petMetadata };

    if (units === 'CNTS') {
        return {
            ...base,
            state: 'counts',
            factor: null,
            missingParams: ['Units is CNTS (BQML or GML required)'],
        };
    }

    if (units === 'GML') {
        if (suvType !== null && suvType !== 'BW') {
            return {
                ...base,
                state: 'preconverted-other',
                factor: null,
                missingParams: [`SUVType is ${suvType} (BW required)`],
            };
        }
        if (missingCorrections.length > 0) {
            // A NAC (or unknown-corrections) GML export: the vendor wrote
            // g/ml pixels, so the honest display unit survives, but the
            // values are NOT usable as SUVbw — same contract as a non-BW
            // normalization, and the SUVbw tooling refuses. mvdicom checks
            // CorrectedImage before ANY SUV path (ct_mr.py:1402-1480);
            // gating the pre-converted path too mirrors that.
            return {
                ...base,
                state: 'preconverted-other',
                factor: null,
                missingParams: missingCorrections,
            };
        }
        if (!input.hasIntensityRange) {
            return {
                ...base,
                state: 'not-quantified',
                factor: null,
                missingParams: ['Degenerate intensity range'],
            };
        }
        return {
            ...base,
            state: 'preconverted-gml',
            factor: 1,
            missingParams: [],
        };
    }

    if (units !== 'BQML') {
        return {
            ...base,
            state: 'not-quantified',
            factor: null,
            missingParams: [
                units === ''
                    ? 'Units (0054,1001) absent'
                    : `Units is ${units} (BQML or GML required)`,
            ],
        };
    }

    if (decayCorrection !== 'START') {
        return {
            ...base,
            state: 'not-quantified',
            factor: null,
            missingParams: [
                decayCorrection === ''
                    ? 'DecayCorrection (0054,1102) absent'
                    : `DecayCorrection is ${decayCorrection} (START required)`,
            ],
        };
    }

    // The NAC hazard: a non-attenuation-corrected reconstruction carries
    // Units=BQML + DecayCorrection=START + the full radiopharmaceutical
    // metadata, so every check above and below passes — only CorrectedImage
    // tells it apart from the AC series, and `raw × factor` on it would
    // present depth-dependently attenuated, quantitatively meaningless
    // values as valid SUVbw. Checked before the factor computation, like
    // mvdicom: the volume is fundamentally ineligible, whatever the
    // conversion inputs say.
    if (missingCorrections.length > 0) {
        return {
            ...base,
            state: 'not-quantified',
            factor: null,
            missingParams: missingCorrections,
        };
    }

    const factor = input.petMetadata
        ? computeSUVbwFactor(input.petMetadata)
        : null;
    if (factor === null) {
        return {
            ...base,
            state: 'not-quantified',
            factor: null,
            missingParams: input.petMetadata
                ? getMissingSUVParams(input.petMetadata)
                : ['PET metadata'],
        };
    }

    if (!input.hasIntensityRange) {
        return {
            ...base,
            state: 'not-quantified',
            factor: null,
            missingParams: ['Degenerate intensity range'],
        };
    }

    return { ...base, state: 'bqml', factor, missingParams: [] };
}
